<!-- eslint-disable max-lines -->
<template>
  <Content :title="$t('DEVELOPER_TITLES.TITLE')">
    <CardApiError
      v-if="apiError"
      :error-text="$t('DEVELOPER_TITLES.API_ERROR')"
      @retry="getDevelopers"
    />

    <div
      v-else
      v-loading="loading"
      class="table-responsive no-hidden-overflow raised"
    >
      <table class="table table--padded table--hover">
        <thead>
          <tr>
            <th>
              {{ $t('DEVELOPER_TITLES.DEVELOPER_TITLE') }}
              <Hint :message="$t('HINTS.SETTING.DEVELOPER_TITLES.TITLE')" />
            </th>
            <th>
              {{ $t('DEVELOPER_TITLES.SLUG') }}
              <Hint :message="$t('HINTS.SETTING.DEVELOPER_TITLES.SLUG')" />
            </th>
            <th>{{ $t('DEVELOPER_TITLES.WEBSITE') }}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="developer in developers" :key="developer.id">
            <td>
              {{ getDeveloperLabel(developer) }}
              <Chip
                v-if="getShouldShowDeveloperChip(developer)"
                :type="getDeveloperChipType(developer)"
              >
                {{ $t(getStateTranslation(developer.state)) }}
              </Chip>
            </td>
            <td class="text-latin">
              {{ getDeveloperSlug(developer) }}
              <Chip
                v-if="developer.slugChangeRequest"
                :type="getDeveloperSlugChipType(developer)"
              >
                {{ $t(getStateTranslation(developer.slugChangeRequest.state)) }}
              </Chip>
            </td>
            <td class="developer-titles__website-cell text-latin">
              {{ getDeveloperWebsite(developer) }}
            </td>
            <td>
              <DeveloperTitlesActions
                :developer="developer"
                @refetch-developers="getDevelopers"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!developers && !loading" class="card">
      <div class="card__block">
        {{ $t('DEVELOPER_TITLES.NO_DEVELOPER') }}
      </div>
    </div>

    <ElButton type="primary" @click="openAddDeveloperTitleModal">
      {{ $t('DEVELOPER_TITLES.ADD_DEVELOPER_TITLE') }}
    </ElButton>

    <AddDeveloperTitleModal
      v-model="showAddDeveloperTitleModal"
      @developer-title-added="getDevelopers"
    />
  </Content>
</template>

<script>
import CardApiError from '@@/common/CardApiError';
import Chip from '@@/common/Chip';
import Content from '@@/common/layout/Content';
import Hint from '@@/common/Hint';

import { DeveloperTitles } from '@/api/api';

import modal from '@/mixins/modal';
import form from '@/mixins/form';
import layout from '@/mixins/layout';
import lang from '@/mixins/lang';
import AddDeveloperTitleModal from './AddDeveloperTitleModal';
import getStateTranslation from '@/utils/settings/getStateTranslation';
import DeveloperTitlesActions from './DeveloperTitlesActions';

export default {
  name: 'DeveloperTitles',

  components: {
    Hint,
    Content,
    CardApiError,
    AddDeveloperTitleModal,
    DeveloperTitlesActions,
    Chip,
  },

  mixins: [modal, form, layout, lang],

  data() {
    return {
      developers: null,
      loading: true,

      showAddDeveloperTitleModal: false,
      showUpdateDetailModal: false,
      showUpdateSlugModal: false,

      apiError: false,
    };
  },

  created() {
    this.setPageTitle('SETTINGS');

    this.getDevelopers();
  },

  methods: {
    getStateTranslation,
    getDeveloperLabel(developer) {
      if (this.lang !== 'fa') {
        return developer.nameEn;
      }
      return developer.nameFa;
    },

    getShouldShowDeveloperChip({ state }) {
      return state && state !== 'A';
    },

    getDeveloperChipType({ state }) {
      if (state === 'R') {
        return 'fail';
      }
      if (state === 'N') {
        return 'warning';
      }
    },

    getDeveloperSlug(developer) {
      if (developer.hasChangedSlug || !developer.slugChangeRequest) {
        return developer.slug;
      }

      return developer.slugChangeRequest.requestedSlug;
    },

    getDeveloperSlugChipType(developer) {
      const { state } = developer.slugChangeRequest;
      if (state === 'R') {
        return 'fail';
      }
      if (state === 'N') {
        return 'warning';
      }
      if (state === 'A') {
        return 'success';
      }
    },

    getDeveloperWebsite(developer) {
      if (this.lang !== 'fa') {
        return developer.websiteEn;
      }
      return developer.websiteFa;
    },

    getDevelopers() {
      this.apiError = false;
      this.loading = true;

      return DeveloperTitles.list()
        .then(({ body: { developers = [] } }) => {
          this.loading = false;
          this.developers = [...developers];
        })
        .catch(({ body: { message } = {} }) => {
          this.loading = false;
          this.apiError = true;

          if (message) {
            this.$message.error(message);
          }
        });
    },

    openAddDeveloperTitleModal() {
      this.openModal('showAddDeveloperTitleModal');
    },
  },
};
</script>
<style lang="scss">
.developer-titles {
  &__website-cell {
    direction: ltr;
    text-align: left;

    @include rtl() {
      text-align: right;
    }
  }

  &__slug-status {
    .chip {
      margin: 0 $global-margin;
    }
  }

  &__actions {
    display: flex;
  }
}
</style>
